/**
 * AerialMovementComponent - floating movement for aerial enemies. 3D movement
 * without navigation, includes hovering and smooth target following.
 */
import * as THREE from 'three';

import { EngineClass } from '../../systems/ClassRegistry.js';
import { property } from '../../utils/serialization/decorator.js';

import { BasePawnMovementComponent } from './BasePawnMovementComponent.js';

import type { SceneComponentOptions } from '../SceneComponent.js';

export interface AerialMovementOptions extends SceneComponentOptions {
  /** Maximum movement speed in units per second */
  maxSpeed: number;
  /** Acceleration lambda for movement */
  accelerationLambda: number;
  /** Deceleration lambda for movement */
  decelerationLambda: number;
  /** Preferred hover height above ground */
  hoverHeight: number;
  /** Random variation in hover height */
  hoverVariation: number;
  /** Rotation speed in radians per second */
  rotationSpeed: number;
  /** Maximum tilt angle when moving (in degrees) */
  maxTiltAngle: number;
}

@EngineClass('AerialMovementComponent')
export class AerialMovementComponent extends BasePawnMovementComponent {


  @property()
  public maxSpeed: number = 15;

  @property()
  public accelerationLambda: number = 3;

  @property()
  public decelerationLambda: number = 2;

  @property()
  public hoverHeight: number = 20;

  @property()
  public hoverVariation: number = 3;

  @property()
  public rotationSpeed: number = 2.0;

  @property()
  public maxTiltAngle: number = 15;

  // Current movement state
  private currentVelocity: THREE.Vector3 = new THREE.Vector3();
  private targetPosition: THREE.Vector3 | null = null;
  private hoverOffset: number = 0;
  private hoverTime: number = 0;
  private lastKnownPlayerPosition: THREE.Vector3 | null = null;
  private searchTime: number = 0;
  private searchRadius: number = 10;

  // Look-at functionality (consistent with NpcMovementComponent)
  private lookAtTarget: THREE.Vector3 | null = null;
  private lookAtThreshold: number = Math.PI / 16; // ~11.25 degrees
  private isLookingAtTarget: boolean = false;

  public override initialize(options?: AerialMovementOptions): void {
    super.initialize(options);
    this.maxSpeed = options?.maxSpeed ?? this.maxSpeed;
    this.accelerationLambda = options?.accelerationLambda ?? this.accelerationLambda;
    this.decelerationLambda = options?.decelerationLambda ?? this.decelerationLambda;
    this.hoverHeight = options?.hoverHeight ?? this.hoverHeight;
    this.hoverVariation = options?.hoverVariation ?? this.hoverVariation;
    this.rotationSpeed = options?.rotationSpeed ?? this.rotationSpeed;
    this.maxTiltAngle = options?.maxTiltAngle ?? this.maxTiltAngle;
    this.hoverOffset = (Math.random() - 0.5) * this.hoverVariation;
  }

  public setTargetPosition(position: THREE.Vector3): void {
    this.targetPosition = position.clone();
    // Adjust target height to hover height
    this.targetPosition.y = this.hoverHeight + this.hoverOffset;
  }

  public setLastKnownPlayerPosition(position: THREE.Vector3 | null): void {
    this.lastKnownPlayerPosition = position ? position.clone() : null;
  }

  public clearTarget(): void {
    this.targetPosition = null;
  }

  public getTargetPosition(): THREE.Vector3 | null {
    return this.targetPosition?.clone() ?? null;
  }

  public override tickPrePhysics(deltaTime: number): void {
    super.tickPrePhysics(deltaTime);

    const owner = this.getActor()!;
    const rootComponent = owner.rootComponent;

    // Update hover animation
    this.hoverTime += deltaTime;
    const hoverAnimation = Math.sin(this.hoverTime * 2) * 0.5; // Gentle up/down motion

    // Get current position
    const currentPosition = new THREE.Vector3();
    rootComponent.getWorldPosition(currentPosition);

    // Determine target position
    let moveTarget: THREE.Vector3 | null = null;

    if (this.targetPosition) {
      // Move toward set target
      moveTarget = this.targetPosition.clone();
      moveTarget.y += hoverAnimation;
    } else if (this.lastKnownPlayerPosition) {
      // Search around last known player position
      this.searchTime += deltaTime;
      const searchAngle = this.searchTime * 0.5; // Slow circular search
      const searchOffset = new THREE.Vector3(
        Math.cos(searchAngle) * this.searchRadius,
        0,
        Math.sin(searchAngle) * this.searchRadius
      );
      moveTarget = this.lastKnownPlayerPosition.clone().add(searchOffset);
      moveTarget.y = this.hoverHeight + this.hoverOffset + hoverAnimation;
    } else {
      // Just hover in place with gentle movement
      moveTarget = currentPosition.clone();
      moveTarget.y = this.hoverHeight + this.hoverOffset + hoverAnimation;
    }

    if (moveTarget) {
      // Calculate desired velocity
      const direction = moveTarget.clone().sub(currentPosition);
      const distance = direction.length();

      if (distance > 0.5) { // Only move if not very close
        direction.normalize();
        const desiredVelocity = direction.multiplyScalar(this.maxSpeed);

        // Smooth velocity interpolation
        this.currentVelocity.lerp(desiredVelocity,
          THREE.MathUtils.damp(0, 1, this.accelerationLambda, deltaTime));
      } else {
        // Close to target, slow down
        this.currentVelocity.lerp(new THREE.Vector3(0, 0, 0),
          THREE.MathUtils.damp(0, 1, this.decelerationLambda, deltaTime));
      }

      // Apply movement
      const deltaPosition = this.currentVelocity.clone().multiplyScalar(deltaTime);
      rootComponent.addWorldPosition(deltaPosition);

      // Handle rotation to face movement direction
      if (this.currentVelocity.length() > 0.1) {
        const horizontalVelocity = this.currentVelocity.clone();
        horizontalVelocity.y = 0;

        if (horizontalVelocity.length() > 0.1) {
          horizontalVelocity.normalize();

          // Check if we have a look-at target that takes priority
          if (this.lookAtTarget && this.isLookingAtTarget) {
            this.handleLookAtTarget();
          } else {
            // Normal movement-based rotation
            // Calculate desired rotation
            const targetRotation = Math.atan2(horizontalVelocity.x, horizontalVelocity.z);

            // Get current rotation
            const currentRotation = new THREE.Euler();
            rootComponent.getWorldRotation(currentRotation);

            // Smooth rotation interpolation
            let rotationDiff = targetRotation - currentRotation.y;

            // Handle angle wrapping
            while (rotationDiff > Math.PI) rotationDiff -= 2 * Math.PI;
            while (rotationDiff < -Math.PI) rotationDiff += 2 * Math.PI;

            const rotationSpeed = this.rotationSpeed * deltaTime;
            const rotationStep = Math.sign(rotationDiff) * Math.min(Math.abs(rotationDiff), rotationSpeed);

            currentRotation.y += rotationStep;
            rootComponent.setWorldRotation(currentRotation);
          }

          // Add subtle tilt when moving (banking effect)
          const tiltAmount = THREE.MathUtils.clamp(
            this.currentVelocity.length() / this.maxSpeed, 0, 1
          ) * THREE.MathUtils.degToRad(this.maxTiltAngle);

          // Tilt in the direction of movement
          const rightVector = new THREE.Vector3(1, 0, 0);
          const currentRotation = new THREE.Euler();
          rootComponent.getWorldRotation(currentRotation);
          rightVector.applyEuler(currentRotation);
          const tiltDirection = rightVector.dot(horizontalVelocity);

          // Apply tilt using local rotation
          const targetTilt = -tiltDirection * tiltAmount;
          rootComponent.rotation.z = THREE.MathUtils.lerp(rootComponent.rotation.z, targetTilt, deltaTime * 3);
        }
      } else {
        // Handle look-at even when not moving
        if (this.lookAtTarget && this.isLookingAtTarget) {
          this.handleLookAtTarget();
        }

        // Level out when not moving
        rootComponent.rotation.z = THREE.MathUtils.lerp(rootComponent.rotation.z, 0, deltaTime * 2);
      }
    }
  }

  public getCurrentVelocity(): THREE.Vector3 {
    return this.currentVelocity.clone();
  }

  public isMovingToTarget(): boolean {
    return this.targetPosition !== null && this.currentVelocity.length() > 0.1;
  }

  public setLookAtTarget(targetPosition: THREE.Vector3, threshold?: number): void {
    this.lookAtTarget = targetPosition.clone();
    if (threshold !== undefined) {
      this.lookAtThreshold = threshold;
    }
    this.isLookingAtTarget = true;
  }

  public clearLookAtTarget(): void {
    this.lookAtTarget = null;
    this.isLookingAtTarget = false;
  }

  public isLookingAtTargetComplete(): boolean {
    if (!this.lookAtTarget || !this.isLookingAtTarget) {
      return true; // No target means "complete"
    }

    const owner = this.getActor()!;
    const currentPosition = new THREE.Vector3();
    owner.rootComponent.getWorldPosition(currentPosition);

    // Calculate direction to target
    const direction = this.lookAtTarget.clone().sub(currentPosition);
    direction.y = 0; // Flatten to XZ plane

    if (direction.length() < 0.001) {
      return true; // Too close to determine direction
    }

    direction.normalize();

    // Get current forward direction
    const currentForward = owner.forwardDirection();
    currentForward.y = 0;
    currentForward.normalize();

    // Calculate angle difference
    const dot = currentForward.dot(direction);
    const cross = currentForward.cross(direction).y;
    const yawDiff = Math.atan2(cross, dot);

    return Math.abs(yawDiff) <= this.lookAtThreshold;
  }

  private handleLookAtTarget(): void {
    if (!this.lookAtTarget) return;

    const owner = this.getActor()!;
    const rootComponent = owner.rootComponent;
    const currentPosition = new THREE.Vector3();
    rootComponent.getWorldPosition(currentPosition);

    // Calculate direction to look-at target
    const direction = this.lookAtTarget.clone().sub(currentPosition);
    direction.y = 0; // Flatten to XZ plane

    if (direction.length() < 0.001) {
      return; // Too close to determine direction
    }

    direction.normalize();

    // Calculate target rotation
    const targetRotation = Math.atan2(direction.x, direction.z);

    // Get current rotation
    const currentRotation = new THREE.Euler();
    rootComponent.getWorldRotation(currentRotation);

    // Smooth rotation interpolation (same as movement rotation)
    let rotationDiff = targetRotation - currentRotation.y;

    // Handle angle wrapping
    while (rotationDiff > Math.PI) rotationDiff -= 2 * Math.PI;
    while (rotationDiff < -Math.PI) rotationDiff += 2 * Math.PI;

    const deltaTime = 1 / 60; // Approximate deltaTime for smooth rotation
    const rotationSpeed = this.rotationSpeed * deltaTime;
    const rotationStep = Math.sign(rotationDiff) * Math.min(Math.abs(rotationDiff), rotationSpeed);

    currentRotation.y += rotationStep;
    rootComponent.setWorldRotation(currentRotation);
  }

  //====================================================================================================
  // Editor Functions
  //====================================================================================================

  public override getEditorClassIcon(): string | null {
    return 'Icon_Aerial';
  }
}